/**
 * @file ProfessionalLogin — /login/profesional
 *
 * Email + password and Google OAuth.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import OAuthButtons from "../components/OAuthButtons";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, Input } from "../ui";

const UNVERIFIED_ERROR = "Debes verificar tu correo electrónico antes de iniciar sesión";

const loginSchema = Yup.object({
  email: Yup.string().email(S.INVALID_EMAIL).required(S.REQUIRED_FIELD),
  password: Yup.string().required(S.REQUIRED_FIELD),
});

const GOOGLE_REDIRECT_ERRORS = {
  google_denied: "Inicio de sesión con Google cancelado.",
  google_invalid: "Error en la respuesta de Google. Intenta de nuevo.",
  google_csrf: "La sesión expiró. Intenta de nuevo.",
  google_exchange: "Error al comunicarse con Google. Intenta de nuevo.",
  google_no_token: "No se recibió respuesta de Google. Intenta de nuevo.",
  google_failed: "Error al iniciar sesión con Google. Intenta de nuevo.",
};

export default function ProfessionalLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, handleAuthSuccess } = useAuth();
  const googleError = GOOGLE_REDIRECT_ERRORS[searchParams.get("error")];
  const [error, setError] = useState(googleError || "");
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (user?.role === "PROFESIONAL" && user?.onboardingComplete === false) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      setError("");
      setUnverifiedEmail("");
      setResendSuccess(false);
      const response = await authService.login(values.email, values.password);
      handleAuthSuccess(response.data);
      navigate(response.data.user.role === "ADMIN" ? "/cx" : "/");
    } catch (err) {
      const msg = err.response?.data?.message || "Error al iniciar sesión";
      setError(msg);
      if (msg === UNVERIFIED_ERROR) setUnverifiedEmail(values.email);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResending(true);
      await authService.resendVerificationEmail(unverifiedEmail);
      setResendSuccess(true);
    } catch {
      setError("Error al reenviar el correo de verificación");
    } finally {
      setResending(false);
    }
  };

  const handleNewGoogleUser = async (data) => {
    try {
      const res = await authService.googleRegister(data.credential, {});
      handleAuthSuccess(res.data);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Error al crear la cuenta");
    }
  };

  return (
    <AuthShell
      title="Ingreso profesional"
      subtitle="Para doctores, dentistas, psicólogos y más"
      footer={
        <RouterLink to="/login" className="auth-back">
          <ArrowLeft size={12} /> Volver
        </RouterLink>
      }
    >
      <OAuthButtons
        role={null}
        onSuccess={(data) => {
          handleAuthSuccess(data);
          navigate("/");
        }}
        onError={(msg) => setError(msg)}
        onNewGoogleUser={handleNewGoogleUser}
      />

      <div className="auth-divider">{S.OAUTH_OR_LOGIN}</div>

      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <div>
            <span>{error}</span>
            {unverifiedEmail && !resendSuccess && (
              <Button
                variant="outline"
                size="sm"
                style={{ marginTop: 8 }}
                onClick={handleResendVerification}
                loading={resending}
              >
                Reenviar correo de verificación
              </Button>
            )}
          </div>
        </div>
      )}
      {resendSuccess && (
        <div className="auth-alert auth-alert-ok">
          <CheckCircle2 size={14} />
          <span>Correo de verificación reenviado. Revisa tu bandeja de entrada.</span>
        </div>
      )}

      <Formik initialValues={{ email: "", password: "" }} validationSchema={loginSchema} onSubmit={handleLogin}>
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form noValidate className="auth-form">
            <Input
              name="email"
              type="email"
              label={S.LOGIN_EMAIL}
              autoComplete="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && errors.email}
            />
            <Input
              name="password"
              type="password"
              label={S.LOGIN_PASSWORD}
              autoComplete="current-password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && errors.password}
            />
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              {S.LOGIN_SUBMIT}
            </Button>
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <RouterLink to="/forgot-password" className="auth-link">
          {S.LOGIN_FORGOT}
        </RouterLink>
        <span className="auth-link-row">
          {S.LOGIN_NO_ACCOUNT}{" "}
          <RouterLink to="/register/professional" className="auth-link">
            {S.LOGIN_REGISTER}
          </RouterLink>
        </span>
      </div>
    </AuthShell>
  );
}
